import { AbilityBuilder, Ability, AbilityClass } from "@casl/ability";

/**
 * List of allowed action types for user permissions.
 * 
 * Represents the type of actions that can be performed within the app.
 */
type Actions =
  | "fill"
  | "view"
  | "create"
  | "update"
  | "delete"
  | "change"
  | "import"
  | "add"
  | "save";

/**
* List of allowed subjects (entities/resources) for user permissions.
*
* These are the resources on which actions can be performed.
*/
export type Subjects =
  | "Clients"
  | "Vdi Client"
  | "All Clients"
  | "Assigned Clients"
  | "Iw Clients"
  | "Tw Clients"
  | "Client"
  | "Roles"
  | "Role"
  | "Buildings"
  | "Building"
  | "Products"
  | "Product"
  | "Plants"
  | "Plant"
  | "Contacts"
  | "Contact"
  | "Components"
  | "Component"
  | "Users"
  | "All Users"
  | "Assigned Users"
  | "User"
  | "Permissions"
  | "Permission"
  | "Disturbances"
  | "Disturbance"
  | "Tasks"
  | "Task"
  | "Template"
  | "Password"
  | "Plant Product"
  | "Excelreport"
  | "Traffic Lights"
  | "Traffic Light"
  | "Subtasks"
  | "Setting Tasks"
  | "Resolvedtasks"
  | "Tasksettings"
  | "Self Current Activities"
  | "Other Current Activities"
  | "Disturbances Client"
  | "Self Upcoming Activities"
  | "Other Upcoming Activities"
  | "Download Acintern"
  | "Download Kunden"
  | "Plant Annual Report"
  | "Visit Task"
  | "Task Reports To"
  | "Micro Settings"
  | "Clients By Dw Mibi Phase"
  | "Apartment Unit Components"
  | "Client Current Activities"
  | "Drinking Water Tasks"
  | "Task Search"
  | "All Drinking Water Buildings"
  | "Plant Status"
  | "Action Taken"
  | "Plant Visit Task"
  | "Product Files"
  | "Task Value";

/**
* Type representing the app's ability configuration using CASL.
*/
// eslint-disable-next-line
export type AppAbility = Ability<[Actions, Subjects]>;

/**
 * Instance of the Ability class configured for the app.
 */
// eslint-disable-next-line
export const AppAbility = Ability as AbilityClass<AppAbility>;

/**
 * Function to define CASL rules for the current user based on their permissions array.
 *
 * @param {any[]} permissions - List of permissions received from the backend.
 * Each permission's name is expected in the format 'action_subject', e.g., 'view_clients'.
 *
 * @returns {RawRule[]} An array of CASL raw rules.
 */
export const defineRulesFor = (permissions: any[]) => {
  const { can, rules } = new AbilityBuilder(AppAbility);
  permissions.forEach((permission) => {
    const { name } = permission;
    const parts = name.split("_");
    if (parts.length >= 2) {
      const action = parts[0];
      const subject = parts
        .slice(1)
        .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      can(action as Actions, subject as Subjects);
    }
  });
  return rules;
};

/**
 * Builds an AppAbility instance using the provided user permissions.
 *
 * @param {any[]} permissions - Array of permissions from backend.
 * @returns {AppAbility} Configured ability object.
 */
export const buildAbilityFor = (permissions: any[]): AppAbility => {
  return new AppAbility(defineRulesFor(permissions), {
    // https://casl.js.org/v5/en/guide/subject-type-detection
    // @ts-ignore
    detectSubjectType: (object) => object!.type,
  });
};
